import {
	Component,
	ComponentInterface,
	Element,
	Listen,
	Prop,
	State,
} from '@stencil/core';
import {
	calculateChildTagName,
	getChildComponent,
	getChildComponents,
} from '../_helpers/prefixes';
import axios from 'axios';
import { AjaxFundSummaryData } from '../_interfaces/fund-card/fund-data';
import { removeInlineStylesFromHead } from '../_helpers/content-security-policy';
import {
	startObserving,
	stopObserving,
} from '../_helpers/moveInlineStylesToHeadObserver';

let observer;

@Component({
	tag: 'ds-featured-funds',
})
export class FeaturedFunds implements ComponentInterface {
	@Element() el: HTMLDsFeaturedFundsElement;
	@Prop() showEsgData = false;

	/**
	 * Are we using AJAX request to API to load data?
	 * If true we use <ds-fund-card-api> child components
	 * If false we use <ds-fund-card> child components
	 */
	@Prop() loadFromApi = false;

	/**
	 * If loadFromApi = true then you can load additional fund data at the tab level by specifying a URL here.
	 */
	@Prop() tabDataAjaxUrl;
	loadedTabIndexes: string[] = []; // Keep track of when tab-level AJAX data is loaded

	@State() selectedTab: HTMLDsTabElement;

	toggleSwitch: HTMLDsToggleSwitchElement;
	fundCards: HTMLDsFundCardElement[] | HTMLDsFundCardApiElement[];
	tabs: HTMLDsTabElement[];
	defaultTabFundsHeight: number = null;

	tabTag: string;

	@Listen('toggleSwitch')
	toggleSwitchHandler(event: CustomEvent) {
		this.toggleEsgData(event.detail.on);
	}

	@Listen('tabSelected')
	async initialiseFundCards(event: CustomEvent) {
		if (!this.loadFromApi) {
			return;
		}

		this.selectedTab = event.detail.tab;

		const fundCards = getChildComponents<HTMLDsFundCardApiElement>(
			this.selectedTab,
			'ds-fund-card-api'
		);
		fundCards.forEach((card) => {
			card.initialise = true;
		});

		this.tabs.forEach(async (tab) => {
			const fundCards = getChildComponents<HTMLDsFundCardApiElement>(
				tab,
				'ds-fund-card-api'
			);
			if (tab === this.selectedTab) {
				if ((await this.hasUnfetchedCards(fundCards)) === false) {
					this.revealFundsInTab(tab, 0);
				}
			} else {
				fundCards.forEach((card) => {
					card.reveal = false;
				});
			}
		});
	}

	async hasUnfetchedCards(
		fundCards: HTMLDsFundCardApiElement[]
	): Promise<boolean> {
		let hasUnfetched = false;
		await Promise.all(
			fundCards.map(async (card) => {
				if ((await card.getHasFetched()) === false) {
					hasUnfetched = true;
				}
			})
		);

		return hasUnfetched;
	}

	/**
	 * Check if all funds in a tab have finished loading, then animate them all in smoothly.
	 * If there is a tabDataAjaxUrl specified, then that AJAX request must have also completed before revealing the cards for any given tab.
	 * This function is invoked every time a fund has completed its AJAX loading.
	 * It is not invoked again when switching to a tab that has had all the necessary data loaded for its funds.
	 */
	revealCardsWhenAllLoaded = async (tab: HTMLDsTabElement) => {
		// Make sure that the summary fund data has been fetched if it's needed.
		if (
			this.loadFromApi &&
			this.tabDataAjaxUrl &&
			(tab.index === null || !this.loadedTabIndexes.includes(tab.index))
		) {
			return;
		}

		const fundCards = getChildComponents<HTMLDsFundCardApiElement>(
			tab,
			'ds-fund-card-api'
		);

		const hasUnfetchedCards = await this.hasUnfetchedCards(fundCards);

		if (!hasUnfetchedCards) {
			/**
			 * Remove cards from the normal flow by changing to position:absolute so we can get a cards height before fading in
			 * This height can be used to provide some animation on the container so that the space needed isn't expanded in too
			 * jerky a way.
			 */
			if (!this.defaultTabFundsHeight) {
				fundCards.forEach((card) => {
					card.classList.add('ds-positioned-absolute');
					card.classList.remove('ds-positioned-relative');
				});
			}

			const loadingSpinner: HTMLDsLoadingSpinnerElement = getChildComponent(
				tab,
				'ds-loading-spinner'
			);

			await loadingSpinner.hide();
			const fundsRow = tab.querySelector<HTMLElement>('.ds-funds-row');
			fundsRow.style.display = 'flex';

			if (!this.defaultTabFundsHeight) {
				this.defaultTabFundsHeight = fundCards[0].clientHeight;
			}

			const fundsContainers =
				this.el.querySelectorAll<HTMLElement>('.ds-funds');
			fundsContainers.forEach((container) => {
				container.style.minHeight = `${this.defaultTabFundsHeight}px`;
			});

			this.revealFundsInTab(tab);
		}
	};

	revealFundsInTab(tab, initialDelay = 400) {
		const fundCards = getChildComponents(tab, 'ds-fund-card-api');
		fundCards.forEach((card, i) => {
			setTimeout(() => {
				card.setAttribute('reveal', ''); // Tell web component to reveal itself
				card.classList.add('ds-positioned-relative');
				card.classList.remove('ds-positioned-absolute');
			}, initialDelay + 40 * i);
		});
	}

	@Listen('loadError')
	handleFundLoadError(event: CustomEvent) {
		event.detail.element.parentElement.style.display = 'none';

		const tab: HTMLDsTabElement = event.detail.element.closest(this.tabTag);
		this.revealCardsWhenAllLoaded(tab);
	}

	@Listen('loadSuccess')
	handleFundLoadSuccess(event: CustomEvent) {
		const tab: HTMLDsTabElement = event.detail.element.closest(this.tabTag);
		this.revealCardsWhenAllLoaded(tab);
	}

	componentWillLoad(): void {
		this.tabTag = calculateChildTagName(this.el, 'ds-tab');
		if (this.loadFromApi) {
			this.tabs = getChildComponents<HTMLDsTabElement>(this.el, this.tabTag);

			this.tabs.forEach((tab, tabIndex) => {
				tab.index = tabIndex.toString();

				if (this.tabDataAjaxUrl) {
					this.fetchTabData(this.tabDataAjaxUrl, tab, tabIndex.toString());
				}
			});
		}
	}

	componentWillRender(): void {
		this.toggleSwitch = getChildComponent(this.el, 'ds-toggle-switch');
		this.fundCards = getChildComponents(
			this.el,
			this.loadFromApi ? 'ds-fund-card-api' : 'ds-fund-card'
		);

		if (this.showEsgData) {
			this.toggleEsgData(true);
		}
	}

	componentDidLoad() {
		if (window.MutationObserver) {
			startObserving(this.el, observer);
		}
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
		if (window.MutationObserver) {
			stopObserving(observer);
		}
	}

	fetchTabData(url, tab: HTMLDsTabElement, tabIndex: string) {
		axios.get(url + `&tabIndex=${tabIndex}`).then((response) => {
			this.loadedTabIndexes.push(tabIndex);
			const fundsInTab = response.data;
			const funds = getChildComponents(tab, 'ds-fund-card-api');
			funds.forEach((fund: HTMLDsFundCardApiElement, fundIndex) => {
				const fundData: AjaxFundSummaryData = fundsInTab[fundIndex];
				if (fundData) {
					fund.domicile = fundData.domicile;
					fund.domicileCode = fundData.domicileCode;
					fund.fundType = fundData.fundType;
				}
			});

			this.revealCardsWhenAllLoaded(tab);
		});
	}

	toggleEsgData(on = false) {
		this.fundCards.forEach((fundCard) => {
			fundCard.toggleEsgData(on);
		});
	}

	[memberName: string]: any;
}
